import React, { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { YoGameEnvironmentRoles } from '../../config/yo-game-environment-roles';
import { Button } from 'react-bootstrap';
import type { YoAuthUser } from '@yakoffice/yakoffice-firebase';
import { useSaveAllController } from '../use-save-all-controller';
import { useItemResult } from '../use-item/use-item';
import { useDeleteUserController } from '../privacy-framework/use-delete-user-controller';
import { useClearPlayerCacheController } from '../use-clear-player-cache-controller/use-clear-player-cache-contorller';
import { useCopyItemController } from '../use-copy-item-controller/use-copy-item-controller';
import { useDataSource } from '../use-data-sources';
import styles from './player-actions.module.sass';
import propertyStyles from '../../components/data-object/components/properties/property-component.module.sass';
import { UrlLinkProperty } from '../../model/data';
import { useShowModal } from '@yakoffice/custom-modal';
import { useGameEnvironments } from '../use-game-environments/use-game-environments';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { ActionData, FloatingActions } from '@yakoffice/floating-actions';


export interface PropTypes {
    authUser: YoAuthUser;
    projectId: string;
    gameEnvironmentId: string;
    itemId: string;
    reRenderItem: () => void;
    itemResult: useItemResult;
    playerProperty?: UrlLinkProperty;
}

export const PlayerActions: FC<PropTypes> = observer((props) => {

    const dataSource = useDataSource();
    const saveAllController = useSaveAllController();
    const deleteUserController = useDeleteUserController();
    const clearPlayerCacheController = useClearPlayerCacheController();
    const copyItemController = useCopyItemController();
    const showModal = useShowModal();
    const gameEnvironments = useGameEnvironments();

    const handleCopyItem = (geId: string, copyToItemId: string) => {
        dataSource && copyItemController.handleCopyItem(props.projectId, dataSource.id, props.gameEnvironmentId, props.itemId, geId, copyToItemId);
    };

    const handleDeleteUser = async () => {
        await deleteUserController.handleStartUserDeletion();
    };

    const handleClearPlayerCache = async () => {
        await clearPlayerCacheController.handleClearPlayerCache();
    };

    const handleCopyPlayer = async () => {

        let targetPlayerId: string;
        let targetGeEnvironment: string = gameEnvironments[0].id;

        const copyPlayer = () => {

            if (!targetPlayerId)
                alert('Player Id cannot be empty');

            handleCopyItem(targetGeEnvironment, targetPlayerId);
        };

        const bodyForm = () => {

            return (
                <div className="p-4">
                    <Row>
                        <Col className={`col-9 ${propertyStyles.propertyComponent}`}>
                            <Form.Label style={{ left: '20px' }}>Target player
                                ID</Form.Label>
                            <Form.Control
                                className={styles.modalGeSelector}
                                type="text"
                                id="search-input"
                                placeholder="Player ID..."
                                onChange={(e) => targetPlayerId = e.target.value}
                                data-testid="txtSearch"
                            />


                        </Col>
                        <Col className={`col-3 d-flex ${propertyStyles.propertyComponent}`}>
                            <Form.Label style={{ left: '20px' }}>Target environment</Form.Label>
                            <Form.Select className={`${styles.modalGeSelector}`}
                                         onChange={e => targetGeEnvironment = e.target.value}>
                                {gameEnvironments.map(ge =>
                                    <option value={ge.id} key={ge.id}>
                                        {ge.name}
                                    </option>
                                )}
                            </Form.Select>
                        </Col>
                    </Row>
                </div>
            );
        };

        showModal({
            show: true,
            title: `Copy ${dataSource?.name}`,
            body: bodyForm(),
            action: <Button variant="primary" onClick={copyPlayer}
                            data-testid="btnModalViewCopied">Copy {dataSource?.name}</Button>,
            canClose: true
        });
    };

    const deleteComponent = () => {

        if (props.authUser.hasGameEnvironmentRoleClaim(props.projectId, props.gameEnvironmentId, YoGameEnvironmentRoles.EditPlayers))
            return {
                icon: 'fas fa-trash',
                callback: handleDeleteUser,
                tooltipText: 'Delete Player',
                colorClass: 'red'
            };
        else
            return undefined;
    };

    const playerActions = () => {

        const actionData: ActionData[] = [];

        if (props.authUser.hasGameEnvironmentRoleClaim(props.projectId, props.gameEnvironmentId, YoGameEnvironmentRoles.EditPlayers) && props.gameEnvironmentId.toLowerCase() !== 'production' && props.projectId.toLowerCase() !== 'ppf')
            actionData.push({
                icon: 'fas fa-tools',
                callback: handleClearPlayerCache,
                tooltipText: 'Clear player cache',
                tooltipArrowProps: { top: '-10px' }
            });

        if (props.authUser.hasGameEnvironmentRoleClaim(props.projectId, props.gameEnvironmentId, YoGameEnvironmentRoles.CopyPlayers))
            actionData.push({
                icon: 'fas fa-copy',
                callback: handleCopyPlayer,
                tooltipText: 'Copy player'
            });

        if (props.authUser.hasGameEnvironmentRoleClaim(props.projectId, props.gameEnvironmentId, YoGameEnvironmentRoles.EditPlayers) && props.playerProperty)
            actionData.push({
                icon: 'fas fa-external-link-square-alt',
                tooltipText: props.playerProperty?.title,
                href: props.playerProperty.value?.toString(),
                externalLink: true
            });

        return actionData;
    };

    return playerActions().length > 0 && deleteComponent() !== undefined &&
        <FloatingActions actions={playerActions()} sideAction={deleteComponent()} />;
});
